import Phaser from 'phaser';
import {
  buildCourse,
  creatureX,
  depthAt,
  maxTreasurePoints,
  stageById,
  toKidsMode,
  TREASURE_POINTS,
  WAIKIKI,
  type Course,
  type StageConfig,
  type Tier,
} from '../sim/course';
import { Records, boardKey, dailyChallengeSeed, todayKey } from '../records';
import type { RunMode } from '../scenes/MenuScene';
import { ReplayPlayer, ReplayRecorder, serializeReplay, type Replay } from '../sim/replay';
import { stageName, t } from '../i18n';
import { shareRun, shareUrl } from '../share';
import { onBeforeFlush } from '../platform/analytics';
import { formatTime, scoreRun, type ScoreBreakdown } from '../sim/score';
import { createSim, progress, step, type SimState } from '../sim/sim';
import { TUNING } from '../sim/tuning';
import {
  analytics,
  createLeaderboard,
  makeDisplayName,
  playerId,
  RemoteLeaderboard,
  type ILeaderboard,
} from '../platform';
import {
  loadMetrics,
  recordFinish,
  recordStart,
  type Phase0Metrics,
} from '../phase0-metrics';
import { AudioEngine } from '../audio/audio';
import { Effects } from './effects';
import { drawHorizon, horizonBottom } from './horizon';
import { InputReader } from './input';
import { GOTHIC, MONO, PALETTE, hex, lerpColor } from './palette';
import { createGameTextures } from './textures';
import { Tutorial } from './tutorial';
import { drawTurtle } from './turtle';

export const VIEW_W = 540;
export const VIEW_H = 960;

/** プレイヤーを画面のどの高さに固定するか。前方（浜側）を広く見せる。 */
const ANCHOR_RATIO = 0.72;
/** 背景の水深グラデーションを塗る帯の高さ（px）。 */
const BAND_PX = 24;
/** 1 フレームで進めるシミュレーションの上限。処理落ち時に暴走させない。 */
const MAX_STEPS_PER_FRAME = 5;
/** 距離ブイを置く間隔（m）。通過するたびに進捗が刻まれる。 */
const BUOY_INTERVAL_M = 50;
/** 一時停止ボタンの位置と大きさ。ミュートの真下、親指で押せる大きさ。 */
const PAUSE_X = VIEW_W - 34;
const PAUSE_Y = 106;
const PAUSE_R = 21;
/** オーバーレイに用意しておく内訳行とボタンの数。作り直さず中身だけ差し替える。 */
/** 指の動かし方を見せる帯。中身が固定なので、位置は決め打ちでよい。 */
const MET_KEY = 'etw:met';
const GUIDE_TOP = 296;
const GUIDE_BOTTOM = 470;
/** 1 段目（横に泳ぐ）。指が X0 と X1 のあいだを往復し、甲羅がついていく。 */
const GUIDE_DRAG_Y = GUIDE_TOP + 22;
const GUIDE_DRAG_X0 = 176;
const GUIDE_DRAG_X1 = 364;
/** 2 段目（潜る）。 */
const GUIDE_TAP_Y = GUIDE_TOP + 112;

const OVERLAY_ROWS = 6;
const OVERLAY_BUTTONS = 3;

type Phase = 'ready' | 'running' | 'paused' | 'finished';

const TIER_COLOR: Record<Tier, number> = {
  shell: PALETTE.shell,
  pearl: PALETTE.pearl,
  coin: PALETTE.coin,
  relic: PALETTE.relic,
};

const TIER_RADIUS: Record<Tier, number> = { shell: 4.5, pearl: 6, coin: 7.5, relic: 9 };

/**
 * ステージごとに固定のシード。自由に走るモードでは毎回同じ海にする。
 * 走るたびに配置が変わると、タイムを縮める練習が成立しない。
 */
function hashString(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function stageSeed(stage: StageConfig): number {
  let h = 2166136261;
  for (let i = 0; i < stage.id.length; i++) {
    h ^= stage.id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export class GameScene extends Phaser.Scene {
  private course!: Course;
  private sim!: SimState;
  private pxPerMeter = 18;
  private anchorY = VIEW_H * ANCHOR_RATIO;

  private reader!: InputReader;
  private recorder!: ReplayRecorder;

  /** 前回の走行。ゴーストとして並走させる。 */
  private ghostReplay: Replay | null = null;
  private ghostPlayer: ReplayPlayer | null = null;
  private ghostSim: SimState | null = null;

  private phase: Phase = 'ready';
  private accumulator = 0;
  private metrics!: Phase0Metrics;
  private board: ILeaderboard = createLeaderboard();
  private records = new Records();
  private mode: RunMode = 'normal';
  private boardKeyForRun = '';
  private abandonReporter: ((reason: string) => void) | null = null;
  /** 巻かれても終わらないことを言ったか。1 回で足りる。 */
  private saidCaughtSafe = false;
  /**
   * 初めて出会ったものの記録。
   *
   * **説明は初対面のときだけ。** 毎回出すと、慣れた人には邪魔でしかない。
   * 端末に残すので、次に開いたときにもう一度説明されることはない。
   */
  private met: Set<string> = new Set();

  /** 海と遠景。いちばん奥。 */
  private gWater!: Phaser.GameObjects.Graphics;
  /** 波・宝・泳者。コースティクスより手前。 */
  private gWorld!: Phaser.GameObjects.Graphics;
  private gHud!: Phaser.GameObjects.Graphics;
  private effects!: Effects;
  private audio!: AudioEngine;
  private tutorial!: Tutorial;
  private txtMute!: Phaser.GameObjects.Text;
  /** 走行中の一時停止ボタン。**走っている最中に必ず抜け道を用意する。** */
  private gPause!: Phaser.GameObjects.Graphics;
  /** HUD 専用カメラ。世界側だけを揺らすために分けている。 */
  private hudCam!: Phaser.Cameras.Scene2D.Camera;
  private buoyLabels: Phaser.GameObjects.Text[] = [];
  /** 泳ぎのストローク位相。速度に応じて進む。 */
  private strokePhase = 0;
  /** 直前フレームで潜行中だったか。浮上の瞬間にしぶきを出すため。 */
  private wasDiving = false;
  /** 巻かれているあいだの回転。もみくちゃにされている様子を出す。 */
  private tumble = 0;
  /** 進路に対する傾き。舵を切っている向きが見えるようにする。 */
  private tilt = 0;
  private prevSimX: number | null = null;
  private txtTime!: Phaser.GameObjects.Text;
  private txtPoints!: Phaser.GameObjects.Text;
  private txtCombo!: Phaser.GameObjects.Text;
  private txtDistance!: Phaser.GameObjects.Text;
  private txtToast!: Phaser.GameObjects.Text;
  /** 手引きの一言。いま出ている合図があるときだけ出る。 */
  private txtCue!: Phaser.GameObjects.Text;
  private overlay!: Phaser.GameObjects.Container;
  private overlayTitle!: Phaser.GameObjects.Text;
  /** 見出しの下に大きく出す 1 つの値（タイム）。タイトルとは行を分ける。 */
  private overlayHero!: Phaser.GameObjects.Text;
  private overlayLead!: Phaser.GameObjects.Text;
  private overlayHint!: Phaser.GameObjects.Text;

  /** 開始前に出す、指の動かし方の絵。 */
  private gGuide!: Phaser.GameObjects.Graphics;
  private guideLabels: Phaser.GameObjects.Text[] = [];
  private guideFinger!: Phaser.GameObjects.Arc;
  private guideTurtle!: Phaser.GameObjects.Arc;
  private guideTap!: Phaser.GameObjects.Arc;
  private guideRing!: Phaser.GameObjects.Arc;
  private guideTweens: Phaser.Tweens.Tween[] = [];
  private overlayRows: { label: Phaser.GameObjects.Text; value: Phaser.GameObjects.Text }[] = [];
  private overlayButtons: Phaser.GameObjects.Text[] = [];
  private buttonActions: ((() => void) | null)[] = [];

  constructor() {
    super('game');
  }

  create(data?: { stageId?: string; mode?: RunMode }): void {
    this.mode = data?.mode ?? 'normal';
    const base = stageById(data?.stageId ?? WAIKIKI.id);
    const stage = this.mode === 'kids' ? toKidsMode(base) : base;
    // リトライごとにコースが変わるとタイムの比較が成立しないので、
    // 走り方ごとにシードを固定する。デイリーだけは日付から全員共通で決める。
    const seed = this.mode === 'daily' ? dailyChallengeSeed() : stageSeed(stage);
    this.boardKeyForRun = boardKey(stage.id, this.mode === 'daily' ? todayKey() : undefined);

    this.records = new Records();
    try {
      const raw = localStorage.getItem(MET_KEY);
      if (raw) this.met = new Set(JSON.parse(raw) as string[]);
    } catch {
      // 読めなければ、もう一度だけ説明する。害はない。
    }
    this.course = buildCourse(stage, seed);
    this.pxPerMeter = VIEW_W / this.course.stage.laneWidth;
    this.anchorY = VIEW_H * ANCHOR_RATIO;

    this.sim = createSim(this.course);
    this.metrics = loadMetrics();
    this.reader = new InputReader(this, this.pxPerMeter);
    this.recorder = new ReplayRecorder(this.course.stage.id, this.course.seed);

    createGameTextures(this);

    // 奥から: 海と遠景 → コースティクス → 波と泳者 → パーティクル → 水中 → 周辺減光
    this.gWater = this.add.graphics().setDepth(0);
    this.effects = new Effects(this, VIEW_W, VIEW_H, 1);
    this.gWorld = this.add.graphics().setDepth(2);

    // 距離ブイの数字。世界に置くので、HUD ではなく世界側カメラに映す。
    const buoyCount = Math.ceil(this.course.stage.length / BUOY_INTERVAL_M) + 1;
    this.buoyLabels = Array.from({ length: buoyCount }, () =>
      this.add
        .text(0, 0, '', { fontFamily: MONO, fontSize: '12px', color: hex(PALETTE.buoyPale) })
        .setOrigin(0.5)
        .setDepth(3)
        .setVisible(false),
    );

    this.audio = new AudioEngine();
    this.tutorial = new Tutorial();
    // 並走するのは「自分の最高の走り」。挙動バージョンが違えば読み込まない。
    this.ghostReplay = this.records.ghost(this.boardKeyForRun);

    this.gHud = this.add.graphics().setDepth(10);

    const monoStyle = { fontFamily: MONO, fontSize: '20px', color: hex(PALETTE.ink) };
    this.txtTime = this.add.text(20, 22, '0:00.00', monoStyle).setDepth(11);
    this.txtPoints = this.add
      .text(VIEW_W - 20, 22, '0 pt', monoStyle)
      .setOrigin(1, 0)
      .setDepth(11);
    // **残り距離は目的そのもの。** 「何をすればいいか分からない」と言われた画面で、
    // ここだけが行き先を示していた。薄い小さな字で出しておく場所ではない。
    this.txtDistance = this.add
      .text(20, 48, '', { fontFamily: MONO, fontSize: '19px', color: hex(PALETTE.ink) })
      .setDepth(11);
    this.txtCombo = this.add
      .text(VIEW_W / 2, 76, '', {
        fontFamily: MONO,
        fontSize: '22px',
        color: hex(PALETTE.swell),
      })
      .setOrigin(0.5, 0)
      .setDepth(11);
    // 手引きの一言。泳者のすぐ下に出す（合図と目が離れないように）。
    this.txtCue = this.add
      .text(VIEW_W / 2, this.anchorY + 96, '', {
        fontFamily: GOTHIC,
        fontSize: '17px',
        color: hex(PALETTE.hint),
        align: 'center',
      })
      .setOrigin(0.5)
      .setVisible(false)
      .setDepth(12);
    this.txtToast = this.add
      .text(VIEW_W / 2, this.anchorY - 90, '', {
        fontFamily: GOTHIC,
        fontSize: '24px',
        color: hex(PALETTE.foam),
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(12);

    // 音の切り替え。触れる大きさを確保するため、余白込みの当たり判定にする。
    this.txtMute = this.add
      .text(VIEW_W - 22, 52, '', { fontFamily: MONO, fontSize: '18px', color: hex(PALETTE.inkDim) })
      .setOrigin(1, 0)
      .setDepth(11)
      .setPadding(14, 10, 6, 10)
      .setInteractive({ useHandCursor: true });
    this.txtMute.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        _x: number,
        _y: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        // ここで止めないと、同じタップがダイブや走行開始にも伝わってしまう。
        event.stopPropagation();
        this.audio.unlock();
        this.audio.setMuted(!this.audio.isMuted);
        this.refreshMuteLabel();
      },
    );
    this.refreshMuteLabel();
    this.input.keyboard?.on('keydown-M', () => {
      this.audio.unlock();
      this.audio.setMuted(!this.audio.isMuted);
      this.refreshMuteLabel();
    });

    // 自動再生は禁止されているので、最初のユーザー操作まで音を作らない。
    this.input.once(Phaser.Input.Events.POINTER_DOWN, () => this.audio.unlock());

    // 帯を敷かずに読ませるので、HUD の文字には濃い影を落とす。
    for (const t of [this.txtTime, this.txtPoints, this.txtDistance, this.txtCombo]) {
      t.setShadow(0, 2, hex(PALETTE.deep), 5, false, true);
    }

    // 走行中の一時停止。ここが無いと、走り出したら最後まで抜けられない。
    //
    // **アイコンを字形に頼らない。** ‖ のような記号は端末のフォントに無いと
    // 何も描かれず、ボタンが消えたようにしか見えない。丸と 2 本の棒を自分で描く。
    // 塗った丸を敷くのは、水面の上でも どんな背景の上でも見えるようにするため。
    // ミュート（「ミュート中」で左へ伸びる）と重ならないよう、真下の段に置く。
    this.gPause = this.add.graphics().setDepth(11).setVisible(false);
    this.gPause.fillStyle(PALETTE.deep, 0.55);
    this.gPause.fillCircle(PAUSE_X, PAUSE_Y, PAUSE_R);
    this.gPause.lineStyle(1.5, PALETTE.inkDim, 0.75);
    this.gPause.strokeCircle(PAUSE_X, PAUSE_Y, PAUSE_R);
    this.gPause.fillStyle(PALETTE.ink, 0.95);
    this.gPause.fillRect(PAUSE_X - 7, PAUSE_Y - 9, 5, 18);
    this.gPause.fillRect(PAUSE_X + 2, PAUSE_Y - 9, 5, 18);
    // 当たり判定は見た目より広く取る。指で確実に押せることを優先する。
    this.gPause.setInteractive(
      new Phaser.Geom.Rectangle(PAUSE_X - 32, PAUSE_Y - 32, 64, 64),
      Phaser.Geom.Rectangle.Contains,
    );
    this.gPause.on(
      'pointerdown',
      (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
        // 止めないと同じタップが「潜る」にも伝わる。
        e.stopPropagation();
        this.showPaused();
      },
    );

    this.buildOverlay();
    this.showReady();

    // 世界と HUD をカメラで分ける。被弾時に世界だけを揺らし、
    // タイムや距離の表示は揺らさないため。
    this.hudCam = this.cameras.add(0, 0, VIEW_W, VIEW_H);
    const worldObjects: Phaser.GameObjects.GameObject[] = [
      this.gWater,
      this.gWorld,
      ...this.effects.objects,
      ...this.buoyLabels,
    ];
    const hudObjects: Phaser.GameObjects.GameObject[] = [
      this.gHud,
      this.txtTime,
      this.txtPoints,
      this.txtDistance,
      this.txtCombo,
      this.txtToast,
      this.txtCue,
      this.txtMute,
      this.gPause,
      this.overlay,
    ];
    this.cameras.main.ignore(hudObjects);
    this.hudCam.ignore(worldObjects);

    this.input.keyboard?.on('keydown-R', () => {
      if (this.phase !== 'running') this.startRun();
    });
    // Esc は「抜ける」ではなく「止める」。走行中の取り消しを 1 段挟む。
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this.phase === 'running') this.showPaused();
      else if (this.phase === 'paused') this.resumeRun();
      else this.toMenu();
    });

    analytics.track('stage_opened', {
      stage: this.course.stage.id,
      mode: this.mode,
      seed: this.course.seed,
    });

    // 離脱地点を取る。通過条件を満たさなかったとき、
    // 「どこで辞めたか」が分からないとコアを直すのかステージを足すのか判断できない。
    // 1 回の走行につき 1 度だけ。ページを閉じるときは複数の経路から
    // 呼ばれうるので、ここで潰さないと離脱を二重に数える。
    let abandonReported = false;
    const reportAbandon = (reason: string) => {
      if (this.phase !== 'running' || abandonReported) return;
      abandonReported = true;
      analytics.track('run_abandoned', {
        stage: this.course.stage.id,
        mode: this.mode,
        reason,
        remainingMeters: Math.round(this.sim.y),
        progress: Math.round(progress(this.sim, this.course) * 100),
        seconds: Math.round(this.sim.t),
      });
    };
    // **自前で pagehide に張らない。** 計測の送信より先に走る保証が要る。
    const stopListening = onBeforeFlush(() => reportAbandon('hidden'));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, stopListening);
    this.abandonReporter = reportAbandon;
  }


  private refreshMuteLabel(): void {
    this.txtMute
      .setText(this.audio.isMuted ? t().muted : '♪')
      .setColor(hex(this.audio.isMuted ? PALETTE.inkFaint : PALETTE.inkDim));
  }

  private openHowTo(): void {
    this.audio.stopMusic();
    this.scene.start('howto', {
      back: 'game',
      stageId: this.course.stage.id.replace('-kids', ''),
      mode: this.mode,
    });
  }

  private toMenu(): void {
    this.abandonReporter?.('menu');
    this.audio.stopMusic();
    this.scene.start('menu', { stageId: this.course.stage.id.replace('-kids', ''), mode: this.mode });
  }

  // ── 画面遷移 ────────────────────────────────

  /**
   * オーバーレイ（開始前・一時停止・結果）。
   *
   * 数値の桁揃えを **等幅フォント + 空白** でやってはいけない。
   * 日本語のラベルは等幅フォントに字が無いのでプロポーショナルへフォールバックし、
   * 空白で作った桁が必ずずれる。ラベルと値を別のテキストにして、
   * 左寄せと右寄せで突き合わせる。
   */
  private buildOverlay(): void {
    // Phaser はシーンのインスタンスを使い回すので、貯め込む配列は毎回空にする。
    // 前回の破棄済み Text が残っていると、次の走行で触った瞬間に落ちる。
    this.overlayRows = [];
    this.overlayButtons = [];
    this.buttonActions = [];
    this.guideLabels = [];
    this.guideTweens = [];

    const panel = this.add.graphics();
    panel.fillStyle(0x04212c, 0.92);
    panel.fillRect(0, 0, VIEW_W, VIEW_H);

    this.overlayTitle = this.add
      .text(VIEW_W / 2, 132, '', {
        fontFamily: GOTHIC,
        fontSize: '32px',
        color: hex(PALETTE.ink),
        align: 'center',
      })
      .setOrigin(0.5, 0);

    // 大きく見せる値（タイム）。数字だけなので等幅でよい。
    this.overlayHero = this.add
      .text(VIEW_W / 2, 186, '', {
        fontFamily: MONO,
        fontSize: '38px',
        color: hex(PALETTE.sand),
      })
      .setOrigin(0.5, 0)
      .setVisible(false);

    this.overlayLead = this.add
      .text(VIEW_W / 2, 196, '', {
        fontFamily: GOTHIC,
        fontSize: '16px',
        color: hex(PALETTE.inkDim),
        align: 'center',
        lineSpacing: 9,
      })
      .setOrigin(0.5, 0);

    // 内訳の行。ラベルは左、値は右。空白での桁揃えはしない。
    const children: Phaser.GameObjects.GameObject[] = [
      panel,
      this.overlayTitle,
      this.overlayHero,
      this.overlayLead,
    ];
    for (let i = 0; i < OVERLAY_ROWS; i++) {
      const label = this.add
        .text(76, 0, '', { fontFamily: GOTHIC, fontSize: '16px', color: hex(PALETTE.inkDim) })
        .setOrigin(0, 0.5)
        .setVisible(false);
      const value = this.add
        .text(VIEW_W - 76, 0, '', { fontFamily: MONO, fontSize: '17px', color: hex(PALETTE.ink) })
        .setOrigin(1, 0.5)
        .setVisible(false);
      this.overlayRows.push({ label, value });
      children.push(label, value);
    }

    // **指の動かし方は、文字ではなく絵で見せる。**
    // 「指を置く → その位置へ泳ぐ」は、読んで分かるより見て分かる方が速い。
    // 6 歳から遊べることを狙っている以上、読ませる前提を最初に置かない。
    this.gGuide = this.add.graphics().setVisible(false);
    children.push(this.gGuide);

    // **同じ線の上を、指が先に、甲羅が遅れて動く。**
    // 別の高さに置くと 2 つの無関係な点に見えて、追いかけていることが伝わらない。
    // 形で区別する — 指は輪（触れている点）、甲羅は塗り（実体）。
    this.guideTurtle = this.add
      .circle(GUIDE_DRAG_X0, GUIDE_DRAG_Y, 10, PALETTE.shellBack)
      .setStrokeStyle(2, PALETTE.shellBackDark)
      .setVisible(false);
    this.guideFinger = this.add
      .circle(GUIDE_DRAG_X0, GUIDE_DRAG_Y, 9)
      .setStrokeStyle(3, PALETTE.hint, 1)
      .setFillStyle()
      .setVisible(false);
    this.guideTap = this.add.circle(VIEW_W / 2, GUIDE_TAP_Y, 7, PALETTE.hint).setVisible(false);
    this.guideRing = this.add
      .circle(VIEW_W / 2, GUIDE_TAP_Y, 9)
      .setStrokeStyle(2, PALETTE.hint, 0.9)
      .setFillStyle()
      .setVisible(false);
    children.push(this.guideTurtle, this.guideFinger, this.guideTap, this.guideRing);

    for (let i = 0; i < 2; i++) {
      const label = this.add
        .text(VIEW_W / 2, 0, '', {
          fontFamily: GOTHIC,
          fontSize: '15px',
          color: hex(PALETTE.inkDim),
        })
        .setOrigin(0.5, 0)
        .setVisible(false);
      this.guideLabels.push(label);
      children.push(label);
    }

    this.overlayHint = this.add
      .text(VIEW_W / 2, 600, '', {
        fontFamily: GOTHIC,
        fontSize: '15px',
        color: hex(PALETTE.inkDim),
        align: 'center',
        lineSpacing: 7,
      })
      .setOrigin(0.5, 0);
    children.push(this.overlayHint);

    // ボタンは押せることが分かる見た目にして、押した先を必ず 1 つに絞る。
    for (let i = 0; i < OVERLAY_BUTTONS; i++) {
      const button = this.add
        .text(VIEW_W / 2, 0, '', {
          fontFamily: GOTHIC,
          fontSize: '19px',
          color: hex(PALETTE.deep),
          backgroundColor: hex(PALETTE.swell),
          padding: { x: 30, y: 14 },
        })
        .setOrigin(0.5)
        .setVisible(false)
        .setInteractive({ useHandCursor: true });
      button.on(
        'pointerdown',
        (_p: Phaser.Input.Pointer, _x: number, _y: number, e: Phaser.Types.Input.EventData) => {
          // ここで止めないと、同じタップが「タップでもう1回」にも伝わる。
          e.stopPropagation();
          this.buttonActions[i]?.();
        },
      );
      this.overlayButtons.push(button);
      children.push(button);
    }

    this.overlay = this.add.container(0, 0, children).setDepth(20);
  }

  /**
   * 画面からはみ出させない。
   *
   * 端末に無いフォントは別のものへ置き換わるので、**幅は書いた時点では決まらない**。
   * 折り返し幅を指定しても、日本語は語の区切りが無いので当てにできない。
   * 実測して、はみ出していたら縮める。これなら字形が何であっても収まる。
   */
  private fitWidth(text: Phaser.GameObjects.Text, maxWidth: number): void {
    text.setScale(1);
    if (text.width > maxWidth) text.setScale(maxWidth / text.width);
  }

  /** 行を並べ直す。使う行だけ見せる。 */
  private setRows(rows: { label: string; value: string; strong?: boolean }[], top: number): number {
    let y = top;
    this.overlayRows.forEach((row, i) => {
      const data = rows[i];
      if (!data) {
        row.label.setVisible(false);
        row.value.setVisible(false);
        return;
      }
      row.label.setVisible(true).setPosition(76, y).setText(data.label);
      row.value
        .setVisible(true)
        .setPosition(VIEW_W - 76, y)
        .setText(data.value)
        .setFontSize(data.strong ? 24 : 17)
        .setColor(hex(data.strong ? PALETTE.sand : PALETTE.ink));
      row.label.setColor(hex(data.strong ? PALETTE.ink : PALETTE.inkDim));
      // 値の幅を先に確保し、ラベルは残りに収める。数字が削れる方が困る。
      this.fitWidth(row.value, (VIEW_W - 152) * 0.5);
      this.fitWidth(row.label, VIEW_W - 152 - row.value.width * row.value.scaleX - 16);
      y += data.strong ? 50 : 38;
    });
    return y;
  }

  /** ボタンを縦に並べる。押し先が 1 つに定まるよう、常に明示的に置く。 */
  /**
   * 指の動かし方の絵。開始前だけ出す。
   *
   * **動かして見せる。** 静止画だと「押しっぱなしで動かす」ことが伝わらない。
   * 甲羅が指を追いかけるところまで見せて初めて、置いた位置へ泳ぐと分かる。
   */
  private showGuide(visible: boolean): void {
    this.guideTweens.forEach((tween) => tween.stop());
    this.guideTweens = [];
    const parts = [this.gGuide, this.guideTurtle, this.guideFinger, this.guideTap, this.guideRing];
    parts.forEach((part) => part.setVisible(visible));
    this.guideLabels.forEach((label) => label.setVisible(visible));
    if (!visible) return;

    const g = this.gGuide;
    g.clear();
    // 泳ぐ道筋。端に矢印を置いて、往復する範囲を示す。
    g.lineStyle(2, PALETTE.inkFaint, 0.7);
    g.beginPath();
    g.moveTo(GUIDE_DRAG_X0 - 16, GUIDE_DRAG_Y);
    g.lineTo(GUIDE_DRAG_X1 + 16, GUIDE_DRAG_Y);
    g.strokePath();
    for (const [x, dir] of [
      [GUIDE_DRAG_X0 - 16, -1],
      [GUIDE_DRAG_X1 + 16, 1],
    ] as const) {
      g.beginPath();
      g.moveTo(x - dir * 8, GUIDE_DRAG_Y - 6);
      g.lineTo(x, GUIDE_DRAG_Y);
      g.lineTo(x - dir * 8, GUIDE_DRAG_Y + 6);
      g.strokePath();
    }
    // 潜る向きの山形。
    g.lineStyle(3, PALETTE.hint, 0.8);
    for (let i = 0; i < 2; i++) {
      const cy = GUIDE_TAP_Y + 22 + i * 10;
      g.beginPath();
      g.moveTo(VIEW_W / 2 - 10, cy);
      g.lineTo(VIEW_W / 2, cy + 7);
      g.lineTo(VIEW_W / 2 + 10, cy);
      g.strokePath();
    }

    this.guideLabels[0]?.setPosition(VIEW_W / 2, GUIDE_DRAG_Y + 42).setText(t().howSteer);
    this.guideLabels[1]
      ?.setPosition(VIEW_W / 2, GUIDE_TAP_Y + 44)
      .setText(t().howDive(TUNING.diveTime.toFixed(2)));
    this.guideLabels.forEach((label) => this.fitWidth(label, VIEW_W - 64));

    this.guideFinger.setPosition(GUIDE_DRAG_X0, GUIDE_DRAG_Y);
    this.guideTurtle.setPosition(GUIDE_DRAG_X0, GUIDE_DRAG_Y);
    this.guideRing.setPosition(VIEW_W / 2, GUIDE_TAP_Y).setScale(1).setAlpha(0.9);

    this.guideTweens.push(
      this.tweens.add({
        targets: this.guideFinger,
        x: GUIDE_DRAG_X1,
        duration: 1400,
        yoyo: true,
        repeat: -1,
        hold: 260,
        repeatDelay: 260,
        ease: 'Sine.easeInOut',
      }),
      this.tweens.add({
        targets: this.guideRing,
        scale: 3.2,
        alpha: 0,
        duration: 900,
        repeat: -1,
        repeatDelay: 500,
        ease: 'Quad.easeOut',
      }),
    );
  }

  private setButtons(items: { label: string; action: () => void; primary?: boolean }[]): void {
    const bottom = VIEW_H - 96;
    this.overlayButtons.forEach((button, i) => {
      const item = items[i];
      if (!item) {
        button.setVisible(false);
        this.buttonActions[i] = null;
        return;
      }
      const y = bottom - (items.length - 1 - i) * 74;
      button
        .setVisible(true)
        .setPosition(VIEW_W / 2, y)
        .setText(item.label)
        .setStyle({
          backgroundColor: hex(item.primary === false ? PALETTE.mid : PALETTE.swell),
          color: hex(item.primary === false ? PALETTE.ink : PALETTE.deep),
        });
      this.fitWidth(button, VIEW_W - 64);
      this.buttonActions[i] = item.action;
    });
  }

  private showReady(): void {
    this.phase = 'ready';
    this.overlay.setVisible(true);
    this.gPause.setVisible(false);
    this.overlayTitle.setText(stageName(this.course.stage.id, this.course.stage.name));
    this.fitWidth(this.overlayTitle, VIEW_W - 48);
    this.overlayHero.setVisible(false);
    // **説明を読ませない。** 以前はここに 8 行の説明文を並べていて、
    // 実際に初めて遊んだ人から「何をすればいいか分からなかった」と返ってきた。
    // 遊んだことのない海の話（青い斜面・オレンジの線）を先に文字で読ませても、
    // 見たことがないものは頭に入らない。
    // ここに残すのは 3 つだけ — 誰なのか、どこへ行くのか、指で何ができるのか。
    // 海の読み方は、その場面が来たときに 1 つずつ出す（`tutorial.ts`）。
    this.overlayLead.setVisible(true).setPosition(VIEW_W / 2, 190).setText(
      [
        this.mode === 'daily' ? t().dailySameSea : t().youAreTurtle,
        t().goal(this.course.stage.length),
      ].join('\n'),
    );
    this.fitWidth(this.overlayLead, VIEW_W - 48);
    this.showGuide(true);
    this.setRows([], 0);
    // 本文の実測の高さから置く。行数と行間の暗算で置くと必ず重なる。
    // 縮めたぶんも効くよう displayHeight を見る。
    this.overlayHint
      .setVisible(true)
      .setPosition(VIEW_W / 2, GUIDE_BOTTOM + 16)
      .setText(
        this.ghostReplay
          ? t().startHintGhost
          : t().startHint,
      );
    this.fitWidth(this.overlayHint, VIEW_W - 48);
    this.setButtons([
      { label: t().start, action: () => this.startRun() },
      // **迷った人がここから読みに行ける。** 見分けが付かないまま海に出すと、
      // 何をしているのか分からないまま 90 秒が過ぎる。
      { label: t().howTo, action: () => this.openHowTo(), primary: false },
      { label: t().toMenu, action: () => this.toMenu(), primary: false },
    ]);
  }

  /** 走行中の一時停止。ここからメニューへ戻れる。 */
  private showPaused(): void {
    if (this.phase !== 'running') return;
    this.phase = 'paused';
    this.audio.stopMusic();
    this.gPause.setVisible(false);
    this.overlay.setVisible(true);
    this.showGuide(false);
    this.overlayTitle.setText(t().paused);
    this.fitWidth(this.overlayTitle, VIEW_W - 48);
    this.overlayHero.setVisible(false);
    this.overlayLead
      .setVisible(true)
      .setPosition(VIEW_W / 2, 200)
      .setText(`${stageName(this.course.stage.id, this.course.stage.name)}\n${t().toShore(Math.max(0, Math.ceil(this.sim.y)))}`);
    this.fitWidth(this.overlayLead, VIEW_W - 48);
    this.setRows([], 0);
    this.overlayHint.setVisible(false);
    this.setButtons([
      { label: t().resume, action: () => this.resumeRun() },
      { label: t().restart, action: () => this.startRun(), primary: false },
      { label: t().toMenu, action: () => this.toMenu(), primary: false },
    ]);
  }

  private resumeRun(): void {
    if (this.phase !== 'paused') return;
    this.phase = 'running';
    this.accumulator = 0;
    this.overlay.setVisible(false);
    this.gPause.setVisible(true);
    this.reader.reset();
    this.audio.unlock();
    this.audio.startMusic();
  }

  private startRun(): void {
    // 直前の走行をゴーストに引き継ぐ。同じシードなので同じ海を走る。
    if (this.ghostReplay) {
      this.ghostPlayer = new ReplayPlayer(this.ghostReplay);
      this.ghostPlayer.reset();
      this.ghostSim = createSim(this.course);
    }

    this.sim = createSim(this.course);
    this.recorder = new ReplayRecorder(this.course.stage.id, this.course.seed);
    this.accumulator = 0;
    this.phase = 'running';
    this.overlay.setVisible(false);
    this.showGuide(false);
    this.gPause.setVisible(true);
    this.reader.reset();
    this.effects.reset();
    this.audio.unlock();
    this.audio.startMusic();
    this.tutorial.reset();
    this.strokePhase = 0;
    this.wasDiving = false;
    this.tumble = 0;
    this.tilt = 0;
    this.prevSimX = null;
    this.cameras.main.setZoom(1);
    this.metrics = recordStart(this.metrics);
    analytics.track('run_started', {
      stage: this.course.stage.id,
      mode: this.mode,
      attempt: this.metrics.starts,
    });
  }

  private finishRun(): void {
    this.phase = 'finished';
    const score = scoreRun(this.sim, this.course);
    this.ghostReplay = this.recorder.finish();
    this.metrics = recordFinish(this.metrics, score.timeSeconds, score.total);

    // **記録は必ず残す。** キッズモードを外すのは*みんなの*ランキングからで、
    // 自己ベストとゴーストまで消す理由は無い。以前はここで一緒に捨てていて、
    // 泳ぎ切っても何も残らなかった。
    // 表は `-kids` を付けた別の鍵になっているので、優しくしたタイムが
    // 通常のタイムと同じ表に混ざることはない。
    const ranked = !this.course.stage.unranked;
    const ranks = this.records.submit(
      this.boardKeyForRun,
      this.course.stage.id,
      score,
      this.ghostReplay,
    );

    // サーバーが設定されていれば投稿する。送るのはスコアではなく入力列で、
    // 採用されるのはサーバーが再生して出した値だけ。
    if (ranked && this.board instanceof RemoteLeaderboard && this.ghostReplay) {
      const id = playerId();
      void this.board
        .submitRun({
          boardKey: this.boardKeyForRun,
          playerId: id,
          displayName: makeDisplayName(hashString(id)),
          claimedTotal: score.total,
          replay: serializeReplay(this.ghostReplay),
        })
        .then((res) => {
          if (!res.ok) analytics.track('submit_rejected', { reason: res.reason ?? 'unknown' });
        });
    }

    const maxPoints = maxTreasurePoints(this.course);
    const rank = (value: number | null) => (value === null ? t().noRank : t().rank(value));

    this.overlay.setVisible(true);
    this.gPause.setVisible(false);
    this.showGuide(false);
    this.overlayTitle.setText(t().finished);
    this.fitWidth(this.overlayTitle, VIEW_W - 48);
    // 見出しとタイムは別の行に置く。1 つのテキストに混ぜると必ず重なる。
    this.overlayHero
      .setVisible(true)
      .setPosition(VIEW_W / 2, 178)
      .setText(formatTime(score.timeSeconds));
    this.overlayLead
      .setVisible(true)
      .setPosition(VIEW_W / 2, 228)
      .setText(t().timeMultiplier(score.timeMultiplier.toFixed(2)));
    this.fitWidth(this.overlayHero, VIEW_W - 48);
    this.fitWidth(this.overlayLead, VIEW_W - 48);

    const bottom = this.setRows(
      [
        { label: t().rowFinish, value: t().points(score.finishBonus) },
        { label: t().rowTreasure(score.treasurePoints, maxPoints), value: t().points(score.treasurePoints) },
        { label: t().rowHatchlings(score.hatchlings), value: t().points(score.hatchlingBonus) },
        { label: t().rowCombo(score.bestCombo), value: t().points(score.comboBonus) },
        { label: t().rowTotal, value: t().points(score.total), strong: true },
      ],
      292,
    );
    this.overlayHint
      .setVisible(true)
      .setPosition(VIEW_W / 2, bottom + 10)
      .setText(t().ranks(rank(ranks.fastest), rank(ranks.treasure), rank(ranks.overall)));
    this.fitWidth(this.overlayHint, VIEW_W - 48);
    // 結果画面では「画面のどこでもタップ」を使わない。
    // どこでも効くと、ボタンを押したつもりが下の再走に取られる。
    this.setButtons([
      { label: t().again, action: () => this.startRun() },
      { label: t().share, action: () => void this.shareResult(score), primary: false },
      { label: t().toMenu, action: () => this.toMenu(), primary: false },
    ]);

    this.audio.stopMusic();
    this.audio.finish();
    analytics.track('run_finished', {
      stage: this.course.stage.id,
      mode: this.mode,
      attempt: this.metrics.starts,
      seconds: Math.round(score.timeSeconds),
      total: score.total,
      hatchlings: score.hatchlings,
    });
  }

  /**
   * 結果を共有する。
   *
   * 押した先で何が起きたかを**そのボタン自身に出す。** 共有シートが出る端末と、
   * クリップボードに写るだけの端末があり、後者は何も起きなかったように見える。
   */
  private async shareResult(score: ScoreBreakdown): Promise<void> {
    const stage = stageName(this.course.stage.id, this.course.stage.name);
    const text = t().shareText(
      stage,
      formatTime(score.timeSeconds),
      score.total,
      this.mode === 'daily',
    );
    const result = await shareRun(text, shareUrl(this.course.stage.id, this.mode));

    analytics.track('run_shared', { stage: this.course.stage.id, mode: this.mode, result });

    const button = this.overlayButtons[1];
    if (!button || result === 'shared' || result === 'cancelled') return;
    const original = button.text;
    button.setText(result === 'copied' ? t().shareCopied : t().shareFailed);
    this.fitWidth(button, VIEW_W - 64);
    this.time.delayedCall(1600, () => {
      if (!button.active) return;
      button.setText(original);
      this.fitWidth(button, VIEW_W - 64);
    });
  }

  // ── ループ ──────────────────────────────────

  update(_time: number, delta: number): void {
    if (this.phase !== 'running') {
      // **手引きの甲羅は、本編と同じ動かし方で追わせる。**
      // tween で追わせると、繰り返すうちに指と重なって「追いかけている」ことが
      // 消える。指へ寄せ続ける方が、実際の操作そのものでもある。
      if (this.guideTurtle?.visible) {
        this.guideTurtle.x = Phaser.Math.Linear(this.guideTurtle.x, this.guideFinger.x, 0.055);
      }
      // 開始前だけは画面のどこでもタップで走り出せる。
      // 結果と一時停止でそれをやると、ボタンを押したタップに再走が重なる。
      const tapped = this.reader.consumeTap();
      if (tapped && this.phase === 'ready') this.startRun();
      this.draw();
      return;
    }

    this.accumulator += delta / 1000;
    let steps = 0;
    while (this.accumulator >= TUNING.dt && steps < MAX_STEPS_PER_FRAME && !this.sim.finished) {
      // record が返す正規形で進める。記録とシミュレーションを一致させ、
      // 次の走行のゴーストが今回の走りと 1 フレームもずれないようにする。
      const input = this.recorder.record(this.reader.read(this.sim.x));
      step(this.sim, this.course, input);
      this.consumeEvents();

      // できたことは二度と教えない。習得の瞬間だけ小さく褒める。
      const learned = this.tutorial.update(this.sim, this.sim.events);
      if (learned) {
        this.effects.sparkleAt(this.screenX(this.sim.x), this.anchorY, 7);
        this.audio.pickup(50);
      }

      if (this.ghostSim && this.ghostPlayer && !this.ghostSim.finished) {
        step(this.ghostSim, this.course, this.ghostPlayer.next());
        this.ghostSim.events.length = 0;
      }

      this.accumulator -= TUNING.dt;
      steps += 1;
    }
    if (steps >= MAX_STEPS_PER_FRAME) this.accumulator = 0;

    this.draw();
    if (this.sim.finished) this.finishRun();
  }

  private consumeEvents(): void {
    for (const event of this.sim.events) {
      switch (event.kind) {
        case 'dive':
          this.effects.splashAt(this.screenX(this.sim.x), this.anchorY, 12);
          this.audio.dive();
          break;
        case 'perfect':
          this.toast(t().dolphin(event.combo), PALETTE.swell);
          this.effects.flashScreen(0.2);
          this.effects.sparkleAt(this.screenX(this.sim.x), this.anchorY, 10);
          this.audio.perfect(event.combo);
          break;
        case 'ride':
          this.toast(t().rodeWave(event.combo), PALETTE.swell);
          this.audio.ride();
          break;
        case 'closeout':
          this.toast(t().breaking, PALETTE.sand);
          this.audio.closeout();
          break;
        case 'sting':
          this.toast(
            this.firstTime('jellyfish') ? t().firstJellyfish : t().jellyfish,
            PALETTE.hazard,
          );
          this.audio.closeout();
          this.effects.splashAt(this.screenX(this.sim.x), this.anchorY, 8);
          break;
        case 'boost':
          this.toast(
            this.firstTime('dolphin') ? t().firstDolphin : t().dolphinRide,
            PALETTE.swell,
          );
          this.audio.ride();
          this.effects.sparkleAt(this.screenX(this.sim.x), this.anchorY, 8);
          break;
        case 'hatchling':
          this.toast(
            this.firstTime('hatchling') ? t().firstHatchling : t().hatchlings(event.carried),
            PALETTE.sand,
          );
          this.audio.pickup(150);
          this.effects.sparkleAt(this.screenX(this.sim.x), this.anchorY, 8);
          break;
        case 'caught':
          // **初めて巻かれたときだけ、終わらないことを言う。**
          // ここで「失敗した」と思って閉じられるのが、いちばん惜しい辞め方。
          if (!this.saidCaughtSafe) {
            this.saidCaughtSafe = true;
            this.toast(t().caughtSafe, PALETTE.hint);
            break;
          }
          this.toast(
            event.lostPoints > 0 ? t().wiped(event.lostPoints) : t().wipedNoLoss,
            PALETTE.hazard,
          );
          this.effects.foamBurst(this.screenX(this.sim.x), this.anchorY);
          this.audio.caught();
          // 世界側カメラだけを揺らす。HUD は別カメラなので数字は読み続けられる。
          this.cameras.main.shake(260, 0.011);
          break;
        case 'pickup':
          this.effects.sparkleAt(
            this.screenX(this.sim.x),
            this.anchorY,
            event.points >= TREASURE_POINTS.coin ? 12 : 5,
          );
          this.audio.pickup(event.points);
          if (this.firstTime('treasure')) {
            this.toast(t().firstTreasure, TIER_COLOR.coin);
          } else if (event.points >= TREASURE_POINTS.coin) {
            this.toast(`+${event.points} pt`, TIER_COLOR.coin);
          }
          break;
        default:
          break;
      }
    }
  }

  /** 初対面なら true を返し、以後は false（覚えたことは二度と言わない）。 */
  private firstTime(what: string): boolean {
    if (this.met.has(what)) return false;
    this.met.add(what);
    try {
      localStorage.setItem(MET_KEY, JSON.stringify([...this.met]));
    } catch {
      // 保存できなくても、この読み込みのあいだは覚えている。
    }
    return true;
  }

  private toast(message: string, color: number): void {
    this.txtToast.setText(message).setColor(hex(color)).setAlpha(1);
    this.tweens.killTweensOf(this.txtToast);
    this.tweens.add({ targets: this.txtToast, alpha: 0, duration: 900, delay: 320 });
  }

  // ── 描画 ────────────────────────────────────

  private screenY(worldY: number): number {
    return this.anchorY + (worldY - this.sim.y) * this.pxPerMeter;
  }

  private screenX(worldX: number): number {
    return worldX * this.pxPerMeter;
  }

  private draw(): void {
    const p = progress(this.sim, this.course);
    const water = this.gWater;
    const world = this.gWorld;
    water.clear();
    world.clear();

    // 遠景の浜を先に置き、海はその下だけを描く。
    // 二重に描かず、近づけば本物の波打ち際とそのまま繋がる。
    const bandBottom = horizonBottom(p, this.screenY(0));
    drawHorizon(water, bandBottom, {
      progress: p,
      lateral: this.sim.x / this.course.stage.laneWidth,
      timeMs: this.time.now,
      viewWidth: VIEW_W,
    });
    this.drawWater(water, bandBottom);

    this.drawBuoys(world, bandBottom);
    this.drawRips(world);
    this.drawSwells(world);
    this.drawCreatures(world);
    this.drawTreasures(world);
    this.drawGhost(world);
    this.drawSwimmer(world);

    // 合図は泳者より手前に。教えているあいだだけ出る。
    if (this.phase === 'running') {
      const cue = this.tutorial.draw(world, {
        sim: this.sim,
        course: this.course,
        screenX: (worldX) => this.screenX(worldX),
        screenY: (worldY) => this.screenY(worldY),
        anchorY: this.anchorY,
        pxPerMeter: this.pxPerMeter,
        timeMs: this.time.now,
      });
      // **出ている合図に一言だけ添える。** 合図が消えれば文字も消える。
      // 説明を先にまとめて読ませるのではなく、要る場面でだけ出す。
      this.txtCue.setText(cue ? { steer: t().cueSteer, ride: t().cueRide, dive: t().cueDive }[cue] : '');
      this.txtCue.setVisible(cue !== null);
    } else {
      this.txtCue.setVisible(false);
    }

    const depthRatio = Math.max(
      0,
      Math.min(1, depthAt(this.course.stage, this.sim.y) / this.course.stage.maxDepth),
    );
    this.effects.update({
      scrollPx: this.sim.y * this.pxPerMeter,
      timeMs: this.time.now,
      diving: this.sim.diveTimer > 0,
      riding: this.sim.riding,
      swimmerX: this.screenX(this.sim.x),
      swimmerY: this.anchorY,
      depthRatio,
    });

    // 崩れの先頭がどれだけ近いかを音に渡す。
    // 画面外から迫るものに耳で備えられるようにするための手がかり。
    let foamProximity = 0;
    for (const swell of this.sim.swells) {
      if (!swell.broken) continue;
      const near = 1 - Math.abs(swell.y - this.sim.y) / 25;
      if (near > foamProximity) foamProximity = near;
    }
    this.audio.update({
      diving: this.sim.diveTimer > 0,
      foamProximity: Math.max(0, Math.min(1, foamProximity)),
    });

    // 速いほどわずかに引く。加速が体で分かるようにするための、控えめな寄り引き。
    const target = 1 + Math.max(0, Math.min(1, this.sim.speed / TUNING.surfSpeed)) * 0.035;
    this.cameras.main.setZoom(Phaser.Math.Linear(this.cameras.main.zoom, target, 0.06));

    this.drawHud(p);
  }

  /**
   * 50 m ごとの距離ブイ。レーンの両端に浮かべ、あいだを破線で結ぶ。
   * 通過するたびに「また 50 m 帰った」という手応えが出る。
   * 数字を読ませるのではなく、くぐる体験として距離を伝えるのが狙い。
   */
  private drawBuoys(g: Phaser.GameObjects.Graphics, bandBottom: number): void {
    const stage = this.course.stage;
    let slot = 0;
    for (let m = BUOY_INTERVAL_M; m < stage.length; m += BUOY_INTERVAL_M) {
      const label = this.buoyLabels[slot++];
      const y = this.screenY(m);
      if (y < bandBottom - 10 || y > VIEW_H + 20) {
        label?.setVisible(false);
        continue;
      }

      const bob = Math.sin(this.time.now / 620 + m) * 2.5;
      const left = this.screenX(1.4);
      const right = this.screenX(stage.laneWidth - 1.4);
      const passed = this.sim.y < m;
      const alpha = passed ? 0.3 : 0.85;

      // 渡された綱。ゲートとして読ませる。
      g.lineStyle(1.5, PALETTE.buoyPale, alpha * 0.35);
      g.beginPath();
      for (let x = left; x <= right; x += 16) {
        const yy = y + bob + Math.sin(x / 30 + this.time.now / 700) * 1.5;
        if (x === left) g.moveTo(x, yy);
        else g.lineTo(x, yy);
      }
      g.strokePath();

      for (const bx of [left, right]) {
        g.fillStyle(PALETTE.buoy, alpha);
        g.fillCircle(bx, y + bob, 7);
        g.fillStyle(PALETTE.buoyPale, alpha);
        g.fillCircle(bx, y + bob - 2, 3.4);
        // 水面に落ちる影で、浮いていることを見せる。
        g.fillStyle(PALETTE.deep, alpha * 0.25);
        g.fillEllipse(bx, y + bob + 9, 16, 5);
      }

      if (label) {
        label
          .setVisible(true)
          .setPosition(left + 26, y + bob)
          .setText(`${m} m`)
          .setAlpha(alpha);
      }
    }
    for (let i = slot; i < this.buoyLabels.length; i++) this.buoyLabels[i].setVisible(false);
  }

  /**
   * 水深に応じた地の色。遠景の帯より下だけを描く。
   * スクロールする等深線と水面のきらめきが、速度と深さの手がかりになる。
   */
  private drawWater(g: Phaser.GameObjects.Graphics, top: number): void {
    const stage = this.course.stage;
    const startY = Math.max(0, Math.floor(top));

    for (let sy = startY; sy < VIEW_H; sy += BAND_PX) {
      const worldY = this.sim.y + (sy + BAND_PX / 2 - this.anchorY) / this.pxPerMeter;
      const depth = depthAt(stage, Math.max(0, worldY));
      const t = Math.max(0, Math.min(1, depth / stage.maxDepth));
      const shallowSide = lerpColor(PALETTE.shore, PALETTE.shallow, Math.min(1, t * 2.4));
      const color = lerpColor(shallowSide, PALETTE.deep, Math.max(0, (t - 0.42) / 0.58));
      g.fillStyle(color, 1);
      g.fillRect(0, sy, VIEW_W, BAND_PX + 1);
    }

    // 10 m ごとの等深線。動きの手がかりになる。
    const first = Math.floor(this.sim.y / 10) * 10;
    g.lineStyle(1, PALETTE.foam, 0.08);
    for (let m = first + 60; m >= first - 60; m -= 10) {
      if (m < 0) continue;
      const y = this.screenY(m);
      if (y < startY || y > VIEW_H + 10) continue;
      g.beginPath();
      g.moveTo(0, y);
      g.lineTo(VIEW_W, y);
      g.strokePath();
    }

    // 水面のきらめき。太陽（遠景の左寄り）に近いほど密にして、光に方向を与える。
    // 矩形で明るい帯を描くと硬い縁が出るので、点の密度で表現する。
    const scroll = this.sim.y * this.pxPerMeter;
    for (let i = 0; i < 34; i++) {
      const bias = ((i * 61) % 100) / 100;
      const gx = VIEW_W * (0.04 + bias * bias * 0.92) + Math.sin(this.time.now / 900 + i) * 14;
      const gy = ((((i * 173) % (VIEW_H + 200)) + scroll * 0.9) % (VIEW_H + 200)) - 100;
      if (gy < startY) continue;
      const twinkle = 0.2 + 0.35 * Math.sin(this.time.now / 240 + i * 2.1);
      if (twinkle <= 0.06) continue;
      g.fillStyle(PALETTE.sun, twinkle * 0.55 * (1 - bias * 0.6));
      g.fillCircle(gx, gy, 1.4 + (i % 3) * 0.6);
    }
  }

  private drawRips(g: Phaser.GameObjects.Graphics): void {
    for (const rip of this.course.rips) {
      const top = this.screenY(rip.yFar);
      const bottom = this.screenY(rip.yNear);
      if (bottom < -40 || top > VIEW_H + 40) continue;
      const x = this.screenX(rip.xCenter - rip.halfWidth);
      const w = rip.halfWidth * 2 * this.pxPerMeter;
      g.fillStyle(PALETTE.hazard, 0.13);
      g.fillRect(x, top, w, bottom - top);
      g.lineStyle(1, PALETTE.hazard, 0.4);
      g.strokeRect(x, top, w, bottom - top);

      // 沖（画面下）向きの矢印で、引かれる方向を明示する。
      const drift = (this.time.now / 22) % 60;
      g.lineStyle(2, PALETTE.hazard, 0.5);
      for (let y = top + drift; y < bottom; y += 60) {
        const cx = x + w / 2;
        g.beginPath();
        g.moveTo(cx, y);
        g.lineTo(cx, y + 22);
        g.lineTo(cx - 6, y + 14);
        g.moveTo(cx, y + 22);
        g.lineTo(cx + 6, y + 14);
        g.strokePath();
      }
    }
  }

  /**
   * 波の状態を 3 段階で描き分ける。何が危険で何が報酬かを一目で分けるのが目的で、
   * 「潜ったのに巻かれた」の主因は、この情報が画面に無かったことだった。
   *
   *   うねり     乗れる斜面をティールで塗る
   *   掘れ上がり 危険の予告。峰にコーラルの線を重ね、斜面はいちばん速い
   *   崩れ       当たり判定のある先頭だけを濃く、後ろに残る泡は薄く
   */
  private drawSwells(g: Phaser.GameObjects.Graphics): void {
    for (const swell of this.sim.swells) {
      const crestY = this.screenY(swell.y);
      if (crestY < -110 || crestY > VIEW_H + 110) continue;

      if (!swell.broken) {
        // 乗れる斜面は浜側（画面では上）に広がる。塗って範囲を教える。
        const faceTop = this.screenY(swell.y - TUNING.faceWindow);
        g.fillStyle(PALETTE.swell, swell.cresting ? 0.22 : 0.14);
        g.fillRect(0, faceTop, VIEW_W, crestY - faceTop);
        g.lineStyle(Math.max(2, swell.amp * 2.2), PALETTE.swell, 0.9);
      } else {
        // 後ろに残る泡。見た目は広いが当たり判定はない。泳いで抜けられる。
        const washBottom = this.screenY(swell.y + TUNING.foamVisualHalf);
        g.fillStyle(PALETTE.foam, 0.16);
        g.fillRect(0, crestY, VIEW_W, washBottom - crestY);

        // 崩れの先頭。ここだけが危険。
        const hit = TUNING.foamHitHalf * this.pxPerMeter;
        g.fillStyle(PALETTE.foam, 0.55);
        g.fillRect(0, crestY - hit, VIEW_W, hit * 2);
        g.fillStyle(PALETTE.hazard, 0.3);
        g.fillRect(0, crestY - hit, VIEW_W, hit * 2);

        // 泡は沖（画面下）から浜（画面上）へ進むので、先に当たるのは上の縁。
        // 「この線が来る」を一目で分かるようにコーラルで縁取る。
        g.lineStyle(2.5, PALETTE.hazard, 0.9);
        g.beginPath();
        for (let x = 0; x <= VIEW_W; x += 10) {
          const y = crestY - hit + Math.sin(x / 58 + swell.id * 1.7 + this.time.now / 620) * 5;
          if (x === 0) g.moveTo(x, y);
          else g.lineTo(x, y);
        }
        g.strokePath();

        g.lineStyle(3, PALETTE.foam, 0.95);
      }

      g.beginPath();
      for (let x = 0; x <= VIEW_W; x += 10) {
        const wobble =
          Math.sin(x / 58 + swell.id * 1.7 + this.time.now / 620) * (swell.broken ? 5 : 3.5);
        const y = crestY + wobble;
        if (x === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.strokePath();

      // 掘れ上がりの予告。峰にコーラルの線を重ね、「次に崩れるのはこれ」を示す。
      if (swell.cresting) {
        g.lineStyle(2, PALETTE.hazard, 0.5 + Math.sin(this.time.now / 140) * 0.25);
        g.beginPath();
        for (let x = 0; x <= VIEW_W; x += 10) {
          const wobble = Math.sin(x / 58 + swell.id * 1.7 + this.time.now / 620) * 3.5;
          const y = crestY + wobble - 5;
          if (x === 0) g.moveTo(x, y);
          else g.lineTo(x, y);
        }
        g.strokePath();
      }

      if (swell.broken) {
        g.fillStyle(PALETTE.foam, 0.7);
        for (let i = 0; i < 9; i++) {
          const fx = ((i * 137 + swell.id * 53) % VIEW_W) + Math.sin(this.time.now / 300 + i) * 6;
          const fy =
            crestY +
            Math.abs(Math.sin(i * 2.1 + swell.id)) * TUNING.foamVisualHalf * this.pxPerMeter * 0.8;
          g.fillCircle(fx, fy, 2.5 + (i % 3));
        }
      }
    }
  }

  /**
   * 生き物。避けるものと頼るものを、色と形で最初から分ける。
   * どちらか分からないまま近づかせるのは、判断ではなく事故になる。
   */
  private drawCreatures(g: Phaser.GameObjects.Graphics): void {
    for (const creature of this.course.creatures) {
      const y = this.screenY(creature.y);
      if (y < -30 || y > VIEW_H + 30) continue;
      const taken = this.sim.touchedCreatures.has(creature.id);
      if (taken && creature.kind !== 'jellyfish') continue;
      const x = this.screenX(creatureX(creature, this.sim.t));
      const t = this.time.now;

      if (creature.kind === 'jellyfish') {
        // 危険はコーラル。波の警告と同じ色にして、意味を覚え直させない。
        const pulse = 0.8 + Math.sin(t / 320 + creature.id) * 0.2;
        g.fillStyle(PALETTE.hazard, taken ? 0.2 : 0.32);
        g.fillCircle(x, y, 13 * pulse);
        g.fillStyle(PALETTE.hazard, taken ? 0.35 : 0.9);
        g.fillEllipse(x, y - 2, 15 * pulse, 12 * pulse);
        g.lineStyle(1.6, PALETTE.hazard, taken ? 0.3 : 0.75);
        for (let i = 0; i < 4; i++) {
          const tx = x - 6 + i * 4;
          g.beginPath();
          g.moveTo(tx, y + 4);
          for (let k = 1; k <= 3; k++) {
            g.lineTo(tx + Math.sin(t / 260 + i + k) * 3, y + 4 + k * 5);
          }
          g.strokePath();
        }
        continue;
      }

      if (creature.kind === 'dolphin') {
        // 頼れるものはティール。乗れる波と同じ色。
        g.fillStyle(PALETTE.swell, 0.25);
        g.fillCircle(x, y, 20);
        g.fillStyle(PALETTE.swell, 1);
        g.fillEllipse(x, y, 15, 30);
        g.fillTriangle(x, y + 14, x - 9, y + 24, x + 9, y + 24);
        g.fillStyle(PALETTE.foam, 0.85);
        g.fillEllipse(x, y - 9, 7, 9);
        continue;
      }

      // 子ガメ。自分の子なので、姿を小さくしただけで同じ形にする。
      drawTurtle(g, x, y, {
        strokePhase: t / 190 + creature.id,
        rotation: 0,
        submerged: 0,
        alpha: 1,
        scale: 0.42,
      });
      g.lineStyle(1.5, PALETTE.sand, 0.4 + Math.sin(t / 300 + creature.id) * 0.2);
      g.strokeCircle(x, y, 17);
    }
  }

  private drawTreasures(g: Phaser.GameObjects.Graphics): void {
    for (const treasure of this.course.treasures) {
      if (this.sim.collected.has(treasure.id)) continue;
      const y = this.screenY(treasure.y);
      if (y < -20 || y > VIEW_H + 20) continue;
      const x = this.screenX(treasure.x);
      const r = TIER_RADIUS[treasure.tier];
      const pulse = 1 + Math.sin(this.time.now / 380 + treasure.id) * 0.12;
      g.fillStyle(TIER_COLOR[treasure.tier], 0.22);
      g.fillCircle(x, y, r * 2.1 * pulse);
      g.fillStyle(TIER_COLOR[treasure.tier], 1);
      g.fillCircle(x, y, r);
      if (treasure.tier === 'relic' || treasure.tier === 'coin') {
        g.lineStyle(1.5, PALETTE.foam, 0.8);
        g.strokeCircle(x, y, r + 4);
      }
    }
  }

  private drawGhost(g: Phaser.GameObjects.Graphics): void {
    const ghost = this.ghostSim;
    if (!ghost) return;
    const y = this.screenY(ghost.y);
    if (y < -40 || y > VIEW_H + 40) return;

    // 前回の自分。同じ姿を単色で薄く重ねる。
    drawTurtle(g, this.screenX(ghost.x), y, {
      strokePhase: ghost.t * 4,
      rotation: 0,
      submerged: ghost.diveTimer > 0 ? 1 : 0,
      alpha: ghost.diveTimer > 0 ? 0.16 : 0.3,
      monotone: PALETTE.ghost,
      scale: 0.92,
    });
  }

  /**
   * プレイヤーのアオウミガメ。
   *
   * ウミガメは生まれた浜へ帰る生き物なので、「沖から浜へ帰還する」という
   * このゲームの目的とそのまま重なる。子ガメを運ぶサブ目標とも噛み合う。
   */
  private drawSwimmer(g: Phaser.GameObjects.Graphics): void {
    const x = this.screenX(this.sim.x);
    const y = this.anchorY;
    const diving = this.sim.diveTimer > 0;
    const stunned = this.sim.stunTimer > 0;
    const speed = Math.max(0, this.sim.speed);
    const deltaSec = this.game.loop.delta / 1000;

    // 前ヒレの羽ばたきは速いほど速く。潜行中は打たずに滑る。
    if (!diving && !stunned) this.strokePhase += deltaSec * (2.2 + speed * 0.85);

    // 舵を切っている向きへ傾ける。
    const lateralSpeed =
      this.prevSimX === null ? 0 : (this.sim.x - this.prevSimX) / Math.max(deltaSec, 1 / 240);
    this.prevSimX = this.sim.x;
    const targetTilt = stunned
      ? 0
      : Math.max(-1, Math.min(1, lateralSpeed / TUNING.lateralSpeed)) * 0.4;
    this.tilt = Phaser.Math.Linear(this.tilt, targetTilt, 0.12);

    // 巻かれているあいだは回し続け、立ち直ったら正面へ戻す。
    if (stunned) this.tumble += deltaSec * 11;
    else this.tumble = Phaser.Math.Linear(this.tumble % (Math.PI * 2), 0, 0.14);

    // 浮上した瞬間のしぶき。
    if (this.wasDiving && !diving) {
      this.effects.splashAt(x, y, 10);
      this.audio.surface();
    }
    this.wasDiving = diving;

    // ── 航跡。速いほど長い ──
    const wake = Math.min(70, 16 + speed * 7);
    if (!stunned) {
      g.fillStyle(PALETTE.foam, diving ? 0.06 : 0.13);
      g.beginPath();
      g.moveTo(x - 9, y);
      g.lineTo(x - 17, y + wake);
      g.lineTo(x + 17, y + wake);
      g.lineTo(x + 9, y);
      g.closePath();
      g.fillPath();
    }

    if (this.sim.riding) {
      // 乗っている間は航跡を光らせ、加速していることを見せる。
      g.fillStyle(this.sim.ridingCresting ? PALETTE.sand : PALETTE.swell, 0.4);
      g.beginPath();
      g.moveTo(x - 6, y);
      g.lineTo(x - 11, y + wake * 1.15);
      g.lineTo(x + 11, y + wake * 1.15);
      g.lineTo(x + 6, y);
      g.closePath();
      g.fillPath();
    }

    // ── 猶予・無敵 ──
    // 「なぜ今の被弾は無かったのか」が分からないと、判定が理不尽に感じられる。
    if (this.sim.graceTimer > 0 && !stunned) {
      g.lineStyle(2, PALETTE.sand, 0.35 + Math.sin(this.time.now / 90) * 0.25);
      g.strokeCircle(x, y, 24);
    }

    if (stunned) {
      // もみくちゃにされている。渦を巻く線を重ねる。
      g.lineStyle(2.5, PALETTE.hazard, 0.85);
      const spin = this.time.now / 80;
      for (let i = 0; i < 3; i++) {
        const a = spin + (i * Math.PI * 2) / 3;
        g.beginPath();
        g.moveTo(x + Math.cos(a) * 14, y + Math.sin(a) * 14);
        g.lineTo(x + Math.cos(a) * 27, y + Math.sin(a) * 27);
        g.strokePath();
      }
    }

    drawTurtle(g, x, y, {
      strokePhase: this.strokePhase,
      rotation: stunned ? this.tumble : this.tilt,
      submerged: diving ? Math.min(1, (TUNING.diveTime - this.sim.diveTimer) * 4) : 0,
      alpha: diving ? 0.8 : 1,
      monotone: stunned ? PALETTE.hazard : undefined,
    });

    // 指の位置を示す目標マーカー。どこへ泳ごうとしているかを見せる。
    if (this.reader.steering && this.phase === 'running') {
      g.lineStyle(1.5, PALETTE.foam, 0.35);
      g.strokeCircle(this.reader.fingerX, y, 22);
    }
  }

  private drawHud(courseProgress: number): void {
    const g = this.gHud;
    g.clear();

    // 上部には帯を敷かない。遠景の浜を覆ってしまい、距離の手がかりが消えるため。
    // 文字の可読性は影で確保している。下部だけ息ゲージのために薄く敷く。
    g.fillStyle(PALETTE.deep, 0.3);
    g.fillRect(0, VIEW_H - 62, VIEW_W, 62);

    // 残り距離のバー（左端、下が沖・上が浜）。
    // 50 m ごとの刻みを入れ、海に浮かぶブイと同じ間隔にして対応を取る。
    const barTop = 118;
    const barBottom = VIEW_H - 110;
    const barH = barBottom - barTop;
    const barX = 12;
    const barW = 10;

    g.fillStyle(PALETTE.foam, 0.16);
    g.fillRect(barX, barTop, barW, barH);

    const stageLength = this.course.stage.length;
    g.fillStyle(PALETTE.foam, 0.3);
    for (let m = BUOY_INTERVAL_M; m < stageLength; m += BUOY_INTERVAL_M) {
      const ty = barBottom - (m / stageLength) * barH;
      g.fillRect(barX - 2, ty, barW + 4, 1);
    }

    const filled = barH * courseProgress;
    g.fillStyle(PALETTE.swell, 0.9);
    g.fillRect(barX, barBottom - filled, barW, filled);

    // 上端は砂。**目的地であることを、色と厚みで示す。**
    g.fillStyle(PALETTE.sand, 1);
    g.fillRect(barX - 4, barTop - 12, barW + 8, 9);
    // 現在地のつまみ。
    g.fillStyle(PALETTE.foam, 1);
    g.fillCircle(barX + barW / 2, barBottom - filled, 6);

    // 息ゲージ。ダイブ 1 回分を切ると色が変わる。
    const gaugeW = 300;
    const gaugeX = (VIEW_W - gaugeW) / 2;
    const gaugeY = VIEW_H - 44;
    const breathRatio = this.sim.breath / TUNING.breathMax;
    const canDive = this.sim.breath >= TUNING.diveCost;
    g.fillStyle(PALETTE.foam, 0.14);
    g.fillRect(gaugeX, gaugeY, gaugeW, 8);
    g.fillStyle(canDive ? PALETTE.swell : PALETTE.hazard, 0.95);
    g.fillRect(gaugeX, gaugeY, gaugeW * breathRatio, 8);
    g.fillStyle(PALETTE.foam, 0.6);
    g.fillRect(gaugeX + gaugeW * (TUNING.diveCost / TUNING.breathMax), gaugeY - 3, 2, 14);

    this.txtTime.setText(formatTime(this.sim.t));
    this.txtPoints.setText(`${this.sim.points} pt`);

    // 残り距離は「あと N m」と言い切る。数字は大きく、単位は小さく。
    this.txtDistance.setText(t().toShore(Math.max(0, Math.ceil(this.sim.y))));
    this.txtDistance.setColor(hex(this.sim.inRip ? PALETTE.hazard : PALETTE.ink));

    this.txtCombo.setText(this.sim.combo > 0 ? t().combo(this.sim.combo) : '');
    this.txtCombo.setColor(hex(this.sim.ridingCresting ? PALETTE.sand : PALETTE.swell));
  }
}

/** 検証用に外へ出す。ヘッドレスの決定論テストが同じ定数を使えるようにする。 */
export const PROTOTYPE_STAGE = WAIKIKI;
export { formatTime };
